<template>
  <div>
  <!-- Uploader Modal -->
  <div v-if="isModalOpen" class="modal-overlay" ref="modalOverlay">
    <div class="modal-window">
      
      <!-- 1. Header -->
      <div class="modal-header">
        <h2 class="modal-title">{{ $t('lbl_EMPBE_Uploader',{mediaType: mediaType==='image' ? $t('lbl_EMPBE_Photos') : $t('lbl_EMPBE_Videos')}) }}</h2>
        <button type="button" @click="handleDiscardClick" class="close-btn" t_label="EMPBE_MediaUploader_Close">
          <span class="material-symbols-outlined" style="font-size: 24px;">close</span>
        </button>
      </div>

      <!-- Error & Warning Popups -->
      <transition-group name="popup-fade" tag="div" class="error-popup-stack">
        <div
          v-for="pop in allPopups"
          :key="pop.key"
          :class="pop.type === 'warning' ? 'warning-popup' : 'error-popup'"
        >
          <div class="error-popup-icon">
            <img v-if="pop.type === 'error'" src="https://cdn.adsalecdn.com/EMP/Backend/svg/PublicTabs/Error.svg" class="warning-icon" />
            <span v-else class="material-symbols-outlined" style="font-size: 20px; color: #F59E0B;">warning</span>
          </div>
          <div class="error-popup-content">
            <span class="error-popup-file-name" v-if="!pop.key.includes('exceed-quota')">{{ activeFile.fileName }}</span>
            <span class="error-popup-text">
              <span v-if="pop.prefix" class="error-popup-prefix">{{ pop.prefix }}: </span>
              <span>{{ pop.detail || pop.message }}</span>
            </span>
            <router-link v-if="pop.key.includes('broken') && mediaType === 'video'" :to="{ name: 'VideoFormatter' }" target="_blank" style="display: flex; align-items: center; gap:4px; margin-top: 8px;">{{ $t('lbl_EMPBE_VideoFormatter_Name') }}
              <span class="material-symbols-outlined" style="font-size: 20px;">open_in_new</span>
            </router-link>
          </div>
          <button type="button" class="error-popup-close" @click="dismissError(pop.key)">
            <span class="material-symbols-outlined" style="font-size: 20px;">close</span>
          </button>
        </div>
      </transition-group>

      <!-- 2. Main Content Split -->
      <div class="modal-body" :style="{padding: filesCount === 0 ? '0' : '1rem'}">
        
        <!-- Left Sidebar: Sequence -->
        <div class="modal-sidebar" :class="{ 'hidden': files.length === 0 }">
          <div class="sidebar-header">
            <span class="sidebar-title" :class="{ 'exceed-limit': hasExceedLimit }">
              {{ sidebarTitle }} ({{ files.length }}/{{ maxQuota }})
            </span>
            
            <label v-show="!isFull" class="upload-label active" t_label="EMPBE_MediaUploader_UploadFile">
              <span class="material-symbols-outlined" style="font-size: 1.5rem;">upload</span>
              {{ $t('lbl_EMPBE_UploaderUploadFile') }}
              <input 
                ref="fileInput"
                type="file" 
                multiple 
                :accept="acceptAttribute" 
                @change="onFileSelect" 
                class="hidden-input" 
              />
            </label>
          </div>
          
          <div class="sidebar-hint">{{ $t('lbl_EMPBE_UploaderDragToReorder') }}</div>

          <div class="sidebar-list">
            <div
              v-for="(file, i) in files"
              :key="file.id"
              class="sidebar-item-wrapper"
            >
              <div
                draggable="true"
                @dragstart="handleSortStart($event, i)"
                @dragenter="handleSortEnter($event, i)"
                @dragend="handleSortEnd"
                @dragover.prevent
                @click="localActiveFileId = file.id"
                class="sidebar-item"
                t_label="EMPBE_MediaUploader_Item"
                :class="{
                  'active': localActiveFileId === file.id,
                  'error-file': fileHasError(file)
                }"
              >
                <!-- Drag Handle -->
                <span class="material-symbols-outlined drag-handle" style="font-size: 20px;">drag_indicator</span>
                
                <!-- Thumb -->
                <div 
                  class="item-thumb"
                  :class="{ 'item-thumb-round': roundBorder }"
                  :style="{ height: fileHasError(file)? '48px' : (48 / parsedAspectRatio) + 'px' }"
                >
                  <div v-if="file.isLoadingInfo" class="LoadingContainer">
                    <LoadingSpinner />
                  </div>
                  <img v-else-if="mediaType === 'image' && !fileHasError(file)"
                    :src="file.previewSrc" />
                  <img v-else-if="mediaType === 'image'"
                    src="https://cdn.adsalecdn.com/EMP/Backend/svg/PublicTabs/warn.svg"
                    class="warning-icon" />
                  <img v-else-if="mediaType === 'video' && file.thumbnail && file.thumbnail.previewSrc"
                    :src="file.thumbnail.previewSrc"
                    class="thumb-img" />
                  <span v-else-if="mediaType === 'video'"
                    class="material-symbols-outlined no-thumb-icon">
                    videocam_off
                  </span>
                </div>
                
                <!-- Info -->
                <div class="item-info">
                  <div class="item-name" :class="{ 'exceed-size': fileHasError(file) }">
                    {{ i + 1 }}. {{ file.fileName }}
                  </div>
                  <div class="item-size">
                    <template>{{ getFileDetails(file) }}</template>
                  </div>
                </div>

                <!-- Remove -->
                <button type="button" @click.stop="removeFile($event, file.id)" class="item-remove" t_label="EMPBE_MediaUploader_Remove">
                  <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
                </button>

                <!-- Cover Tag -->
                <div v-if="i === 0 && isShowCover" class="item-cover-tag">
                  ★ {{ mediaType === 'image' ? $t('lbl_EMPBE_UploaderCover') : $t('lbl_EMPBE_Uploader_MainVideo') }}
                </div>
              </div>
              
              <!-- Error Message -->
              <div v-if="fileHasError(file)" class="item-error-message">
                <div v-if="fileHasWrongFormat(file)">
                  {{ $t('lbl_EMPBE_Uploader_Error_Format')}}
                </div>
                <div v-if="fileExceedsMaxSize(file)">
                  {{ $t('lbl_EMPBE_Uploader_Error_Size', { limitedSize: maxSize }) }}
                </div>
                <div v-if="mediaType === 'video' && fileExceedsMaxDuration(file)">
                  {{ $t('lbl_EMPBE_Uploader_Error_Duration', { limitedDuration: maxDuration }) }}
                </div>
                <div v-if="file.isBroken && !fileHasWrongFormat(file)">
                  {{ $t('lbl_EMPBE_FileBroken') || 'File is broken' }}
                </div>
              </div>
              <div v-else-if="getRejectReasonForFile(file)" class="item-error-message">
                {{ $t('lbl_EMPBE_RejectedReason') }}: {{ getRejectReasonForFile(file) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Right Area: Editor -->
         <div class="modal-editor-container">
          <div 
            class="modal-editor"
            :class="{ 'image-editor-bg': mediaType === 'image', 'video-editor-bg': mediaType === 'video' }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
          >
            <!-- Empty State -->
            <div 
              v-if="(!activeFile || fileHasError(activeFile) || isDraggingFile) && !isFull"
              class="empty-state" 
              :style="{ backgroundColor: isDraggingFile ? '#F1F0FF' : (filesCount === 0 || fileHasError(activeFile) ? '#F2F3F5' : 'white') }"
            >
              <span class="material-symbols-outlined empty-icon" style="font-size: 80px; color: #7366FF;">upload</span>
              <p class="empty-state-title">
                {{ $t('lbl_EMPBE_UploaderEmptyState1') }}
              </p>
              <p class="empty-state-description">
                {{ $t('lbl_EMPBE_UploaderEmptyState2') }}
                <span @click="openFileExplorer" class="browse-link" t_label="EMPBE_MediaUploader_UploadFile_Here">{{ $t('lbl_EMPBE_UploaderEmptyState2_Here') }}{{ $t('lbl_EMPBE_UploaderEmptyState2_Upload') }}</span>
                
              </p>
              <p v-if="requirementMessage" class="empty-state-requirement-message">{{ requirementMessage}}</p>
            </div>

            <!-- Image Editor -->
            <img-editor
              v-if="mediaType === 'image' && activeFile && !fileHasError(activeFile) && !isDraggingFile"
              :active-file="activeFile"
              :aspect-ratio-str="aspectRatioStr"
              :max-size="maxSize"
              :is-cover="isActiveFileCover"
              :is-show-cover="isShowCover"
              :round-border="roundBorder"
              @update="handleImageEditorUpdate"
              @make-cover="handleMakeCover"
              @minZoomChange="onEditorMinZoomChange"
            />
            

            <!-- Video Editor -->
            <video-editor
              v-if="mediaType === 'video' && activeFile && !fileHasError(activeFile) && !isDraggingFile"
              ref="videoEditor"
              :active-file="activeFile"
              :aspect-ratio-str="aspectRatioStr"
              :is-cover="isActiveFileCover"
              :is-show-cover="isShowCover"
              :is-show-video-preview-background="isShowVideoPreviewBackground"
              :emptyStateBackgroundColor="filesCount === 0 ? 'transparent' : 'white'"
              @thumbnail-update="handleThumbnailUpdate"
              @capture-thumbnail="(ve) => handleCaptureThumbnail(ve)"
              @upload-thumbnail="handleUploadThumbnail"
              @make-cover="handleMakeCover"
            />
          </div>
        </div>
      </div>

      <!-- 3. Footer -->
      <div class="modal-footer">
        <button type="button" @click="handleDiscardClick" class="btn btn-danger-outline" t_label="EMPBE_MediaUploader_Discard">
          {{ $t('lbl_EMPBE_UploaderDiscard') }}
        </button>
        <button 
          type="button"
          @click="() => confirmUpload()"
          :disabled="isProcessing || hasExceedLimit || hasError"
          class="btn btn-primary btn-confirm-action"
          t_label="EMPBE_MediaUploader_Confirm"
        >
          <span v-if="isProcessing" class="material-symbols-outlined animate-spin" style="font-size: 20px;">progress_activity</span>
          <span>{{ $t('lbl_EMPBE_UploaderConfirm') }}</span>  
        </button>
      </div>

    </div>

    <!-- Confirmation Dialog -->
    <div v-if="showDiscardConfirm" class="confirm-dialog-overlay" @click.self="showDiscardConfirm = false">
      <div class="confirm-dialog">
        <h3 class="confirm-dialog-title">{{ $t("lbl_EMPBE_ConfirmToDiscardChanges") }}</h3>
        <p class="confirm-dialog-message">{{ $t("lbl_EMPBE_ConfirmToDiscardChanges_Content") }}</p>
        <div class="confirm-dialog-actions">
          <button type="button" @click="confirmDiscard" class="btn btn-cancel" t_label="EMPBE_MediaUploader_Confirm_Discard">
            {{ $t("lbl_EMPBE_Discard") }}
          </button>
          <button type="button" @click="showDiscardConfirm = false" class="btn btn-primary btn-confirm" t_label="EMPBE_MediaUploader_Confirm_KeepEditing">
            {{ $t("lbl_EMPBE_KeepEditing") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
// --- COMPONENTS ---
import ImgEditor from './imgEditor.vue';
import VideoEditor from './videoEditor.vue';
import LoadingSpinner from '../../../loadingSpinner.vue';
import {
  getFormatedSizeInMb,
  getThumbnailBlob,
  getVideoDuration,
  formatDuration,
  getFileSizeFromCDN,
  checkIsCdnLink,
  getDefaultEditState,
  getMediaUploaderModalImageDataObj,
  getMediaUploaderModalVideoDataObj,
  getNewMediaUploaderModalImageDataObj,
  getNewMediaUploaderModalVideoDataObj,
  getBaseId,
  getCroppedImgInfo,
  captureFrameFromVideo
} from '@/_helpers/media-uploader-utils.js';

export default {
  name: 'MediaUploadModal',
  components: {
    ImgEditor,
    VideoEditor,
    LoadingSpinner
  },
  props: {
    mediaType: {
      type: String,
      required: true,
      validator: (value) => ['image', 'video'].includes(value)
    },
    medias: {
      type: Array,
      default: () => []
    },
    maxQuota: {
      type: Number,
      default: 10
    },
    aspectRatioStr: {
      type: String,
      default: "1:1"
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 500
    },
    maxDuration: {
      type: Number,
      default: null
    },
    requirementMessage: {
      type: String,
      default: ''
    },
    isReturnBase64: {
      type: Boolean,
      default: false
    },
    isShowCover: {
      type: Boolean,
      default: false
    },
    roundBorder: {
      type: Boolean,
      default: false
    },
    isShowVideoPreviewBackground: {
      type: Boolean,
      default: false
    },
    rejectRecords: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      files: [],
      fileSnapshots: [],
      isProcessing: false,
      dragItem: null,
      dragOverItem: null,
      isDraggingFile: false,
      localActiveFileId: null,
      showDiscardConfirm: false,
      dismissedErrors: [],
      editorMinZoom: 0,
      isModalOpen: false,
    };
  },
  computed: {
    activeFile() {
      return this.files.find(f => f.id === this.localActiveFileId);
    },
    filesCount() {
      return this.files.length;
    },
    parsedAspectRatio() {
      const parts = this.aspectRatioStr.split(':').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && parts[1] !== 0) {
        return parts[0] / parts[1];
      }
      return this.mediaType === 'video' ? 0.5625 : 1;
    },
    isFull() {
      return this.files.length >= this.maxQuota;
    },
    hasExceedLimit() {
      return this.files.length > this.maxQuota;
    },
    acceptAttribute() {
      return this.mediaType === 'image' ? 'image/*' : 'video/*';
    },
    sidebarTitle() {
      return this.mediaType === 'image' ? this.$t('lbl_EMPBE_Photos') : this.$t('lbl_EMPBE_Videos');
    },
    isActiveFileCover() {
      if (!this.activeFile || this.files.length === 0) return false;
      return this.files[0].id === this.activeFile.id;
    },
    hasError() {
      return this.files.some(f => this.fileHasError(f));
    },
    fileFormat() {
      return this.mediaType === 'image' ? 'JPG, PNG' : 'FLV, MP4, MPEG';
    },
    activeFileErrors() {
      const errors = [];
      const mediaTypeLabel = this.mediaType === 'image' ? this.$t('lbl_EMPBE_Photos') : this.$t('lbl_EMPBE_Videos');
      if (this.hasExceedLimit) {
        errors.push({
          key: 'exceed-quota',
          message: this.$t('lbl_EMPBE_Uploader_Error_ExceedQuota', { maxQuota: this.maxQuota, mediaType: mediaTypeLabel })
        });
      }
      const file = this.activeFile;
      if (file) {
        if (this.fileHasWrongFormat(file)) {
          errors.push({
            key: `${file.id}-format`,
            prefix: this.$t('lbl_EMPBE_UploadFailed'),
            detail: this.$t('lbl_EMPBE_Uploader_Error_Format_Long', { fileFormat: this.fileFormat })
          });
        }
        if (this.fileExceedsMaxSize(file)) {
          errors.push({
            key: `${file.id}-size`,
            prefix: this.$t('lbl_EMPBE_UploadFailed'),
            detail: this.$t('lbl_EMPBE_Uploader_Error_Size', { limitedSize: this.maxSize })
          });
        }
        if (this.fileExceedsMaxDuration(file)) {
          errors.push({
            key: `${file.id}-duration`,
            prefix: this.$t('lbl_EMPBE_UploadFailed'),
            detail: this.$t('lbl_EMPBE_Uploader_Error_Duration', { limitedDuration: this.maxDuration })
          });
        }
        if (file.isBroken && !this.fileHasWrongFormat(file)) {
          errors.push({
            key: `${file.id}-broken`,
            prefix: this.$t('lbl_EMPBE_UploadFailed'),
            detail: this.$t('lbl_EMPBE_FileBroken') || 'The file is broken or corrupted and cannot be opened.'
          });
        }
      }
      return errors.filter(e => !this.dismissedErrors.includes(e.key));
    },
    activeFileRejections() {
      const file = this.activeFile;
      const reason = this.getRejectReasonForFile(file);
      if (!file || !reason) return [];
      return [{
        key: `${file.id}-reject-reason`,
        prefix: this.$t('lbl_EMPBE_RejectedReason'),
        detail: reason,
      }].filter(e => !this.dismissedErrors.includes(e.key));
    },
    editorWarnings() {
      if (this.mediaType !== 'image' || !this.activeFile || this.fileHasError(this.activeFile)) return [];
      if (this.editorMinZoom < 3) return [];
      return [{ key: 'file-too-small', message: this.$t('lbl_EMPBE_Uploader_FileTooSmallToScale') }]
        .filter(w => !this.dismissedErrors.includes(w.key));
    },
    allPopups() {
      const errors = this.activeFileErrors.map(e => ({ ...e, type: 'error' }));
      const rejections = this.activeFileRejections.map(r => ({ ...r, type: 'warning' }));
      const warnings = this.editorWarnings.map(w => ({ ...w, type: 'warning' }));
      return [...errors, ...rejections, ...warnings];
    }
  },
  watch: {
    medias: {
      immediate: true,
      async handler(newMedias) {
        if (!newMedias || newMedias.length === 0) {
          this.files = [];
          this.fileSnapshots = [];
          return;
        };

        if (this.mediaType === 'image') {
          await this.setImages(newMedias);
        }

        if (this.mediaType === 'video') {
          await this.setVideos(newMedias);
        }
      }
    },
    localActiveFileId() {
      this.dismissedErrors = [];
      this.editorMinZoom = 0;
    },
    'files.length'() {
      this.dismissedErrors = this.dismissedErrors.filter(k => k !== 'exceed-quota');
    },
    isModalOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.modalOverlay) {
            document.body.appendChild(this.$refs.modalOverlay);
          }
        });
      } else {
        this.$emit('modelClosed');
      }
    }
  },
  mounted() {
    if (this.isModalOpen && this.$refs.modalOverlay) {
      document.body.appendChild(this.$refs.modalOverlay);
    }
  },
  beforeDestroy() {
    if (this.$refs.modalOverlay && this.$refs.modalOverlay.parentNode === document.body) {
      document.body.removeChild(this.$refs.modalOverlay);
    }
  },
  methods: {
    dismissError(key) {
      this.dismissedErrors.push(key);
    },
    getRejectReasonForFile(file) {
      if (!file) return '';
      const rec = (this.rejectRecords || []).find(
        (r) => r && String(r.mediaId) === String(file.id)
      );
      const reason = rec && rec.rejectReason != null ? String(rec.rejectReason) : '';
      return reason.trim() ? reason : '';
    },
    onEditorMinZoomChange(val) {
      this.editorMinZoom = val;
    },
    fileHasError(file){
      if(!file) return false;
      
      if (file.isBroken) return true;

      if (this.mediaType === 'image') {
        return this.fileExceedsMaxSize(file) || this.fileHasWrongFormat(file);
      } else if (this.mediaType === 'video') {
        return this.fileExceedsMaxSize(file) || this.fileHasWrongFormat(file) || this.fileExceedsMaxDuration(file) || this.CannotCaptureThumbnail(file);
      }

      return false;
    },
    fileExceedsMaxSize(file) {
      if (!file) return false;
      const maxSizeBytes = this.maxSize * 1024 * 1024;
      const fileSizeBytes = file.sizeBytes ?? 0;
      return fileSizeBytes > maxSizeBytes;
    },
    CannotCaptureThumbnail(file) {
      if (!file || this.mediaType !== 'video') return false;
      if (this.mediaType === 'video') {
        if (!file.isLoadingInfo && (!file.thumbnail || !file.thumbnail.previewSrc || file.thumbnail.isBroken)) {
          file.isBroken = true;
          return true;
        }
      }
      return false;
    },
    fileExceedsMaxDuration(file) {
      if (this.maxDuration === null || this.mediaType !== 'video' || !file || file.duration === 0) return false;
      return file.duration > this.maxDuration;
    },
    fileHasWrongFormat(file) {
      if (!file) return false;

      // Check if this is a processed file object with an original file reference
      const fileType = file.isPendingUpload ? (file.pendingUploadFile?.type ?? "") : "";
      // If we have a type, check if it matches the expected media type
      if (fileType) {
        const filterType = this.mediaType === 'image' ? 'image/' : this.mediaType === 'video' ? 'video/' : '';
        return !fileType.startsWith(filterType);
      }

      // Get file name (could be from processed object or original File object)
      const fileName = file.isPendingUpload ? (file.pendingUploadFile?.name?.toLowerCase() ?? "") : file.fileName?.toLowerCase();
      // If no type, check file extension as fallback (especially for SVG files)
      if (fileName) {
        if (this.mediaType === 'image') {
          const imageExtensions = [ 
            '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico', '.tif', '.tiff', '.heic', '.heif', '.jfif', '.pjpeg', '.pjp', '.avif', '.apng', '.raw', '.ras', '.cmx', '.dds', '.emf', '.exif', '.ico', '.j2k', '.jpf', '.jpx', '.jpm', '.jxr', '.wdp', '.cur', '.ai', '.eps', '.psd'
          ];
          return !imageExtensions.some(ext => fileName.endsWith(ext));
        } else if (this.mediaType === 'video') {
          const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
          return !videoExtensions.some(ext => fileName.endsWith(ext));
        }
      }
      
      // If no type and no valid extension found, consider it wrong format
      return true;
    },

    async setImages(images) {
      this.files = images.map((f) => getMediaUploaderModalImageDataObj(f));
      // console.log("this.files - Images: ", this.files);
      const processeds = await Promise.allSettled(images.map(async (image) => {
        const sizeBytes = await getFileSizeFromCDN(image.cdnLink);
        const sizeFormatted = sizeBytes > 0
          ? getFormatedSizeInMb(sizeBytes) + ' MB'
          : '0 MB';
        
        return {
          id: image.id,
          sizeFormatted,
          sizeBytes,
          isBroken: sizeBytes <= 0,
        };
      }));
      // console.log("processeds - Images: ", processeds);
      this.$nextTick(() => {
        for (const f of this.files) {
          const processed = processeds.find(p => p.status === 'fulfilled' && p.value && p.value.id === f.id);
          if (processed) {
            f.sizeFormatted = processed.value.sizeFormatted;
            f.sizeBytes = processed.value.sizeBytes;
            f.isBroken = processed.value.isBroken;
          }
          f.isLoadingInfo = false;
        }

        this.fileSnapshots = [...this.files.map((f) => ({ ...f }))];
        // console.log("Updated Images - this.files: ", this.files);
        // console.log("Updated Images - this.fileSnapshots: ", this.fileSnapshots);
      });
    },

    async setVideos(videos) {
      this.files = videos.map((f) => getMediaUploaderModalVideoDataObj(f));
      // console.log("this.files - Videos: ", this.files);
      const processeds = await Promise.allSettled(videos.map(async (video) => {
        const sizeBytes = await getFileSizeFromCDN(video.cdnLink);
        const sizeFormatted = sizeBytes > 0
          ? getFormatedSizeInMb(sizeBytes) + ' MB'
          : '0 MB';
        const duration = await getVideoDuration(video.cdnLink);
        
        return {
          id: video.id,
          sizeFormatted,
          sizeBytes,
          duration,
          durationFormatted: formatDuration(duration),
          isBroken: sizeBytes <= 0 || duration <= 0,
        };
      }));
      // console.log("processeds - Videos: ", processeds);
      this.$nextTick(() => {
        for (const f of this.files) {
          const processed = processeds.find(p => p.status === 'fulfilled' && p.value && p.value.id === f.id);
          if (processed) {
            f.sizeFormatted = processed.value.sizeFormatted;
            f.sizeBytes = processed.value.sizeBytes;
            f.duration = processed.value.duration;
            f.durationFormatted = processed.value.durationFormatted;
            f.isBroken = processed.value.isBroken;
          }
          f.isLoadingInfo = false;
        }

        this.fileSnapshots = [...this.files.map((f) => ({ ...f }))];
        // console.log("Updated Images - this.files: ", this.files);
        // console.log("Updated Images - this.fileSnapshots: ", this.fileSnapshots);
      });
    },

    onFileSelect(e) {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFiles = Array.from(e.target.files);
        if (this.mediaType === 'image') {
          this.processImageFiles(selectedFiles);
        } else if (this.mediaType === 'video') {
          this.processVideoFiles(selectedFiles);
        }
      }
      e.target.value = null;
    },

    async processImageFiles(newFiles) {
      const baseId = getBaseId();
      const processed = [];

      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];
        const id = `${baseId}-${i}`;
        const newFileDataObj = getNewMediaUploaderModalImageDataObj(id, file, file.name, file.size);

        processed.push(newFileDataObj);
        this.files.push(newFileDataObj);
      }

      this.$nextTick(() => {
        const firstValid = processed.find(f => !f.isBroken && !this.fileHasError(f));
        if (firstValid) {
          this.localActiveFileId = firstValid.id;
        }
        // console.log("processed images: ", processed);
      });
    },

    async processVideoFiles(newFiles) {
      const baseId = getBaseId();
      const processing = [];

      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];
        const id = `${baseId}-${i}`;
        const newFileDataObj = getNewMediaUploaderModalVideoDataObj(id, file, file.name, file.size);

        processing.push(newFileDataObj);
        this.files.push({ ...newFileDataObj, isLoadingInfo: true });
      }
      // console.log("processVideoFiles this.files: ", this.files);
      // console.log("processVideoFiles processing: ", processing);
      const processeds = await Promise.allSettled(processing.map(async (video) => {
        const { videoDuration, blob, extension } = await getThumbnailBlob(video.previewSrc, 1);
        const parts = video.fileName.split('.');
        parts.pop();
        const thumbnailFileName = parts.join('.') + `.${extension}`;
        const newThumbnailFileDataObj = getNewMediaUploaderModalImageDataObj(`${video.id}-thumbnail`, blob, thumbnailFileName, blob.size);
        
        return {
          id: video.id,
          duration: videoDuration,
          durationFormatted: formatDuration(videoDuration),
          thumbnail: newThumbnailFileDataObj,
          isBroken: videoDuration <= 0 || newThumbnailFileDataObj.isBroken
        };
      }));
      // console.log("processVideoFiles processeds: ", processeds);
      this.$nextTick(() => {
        for (const f of this.files) {
          const processed = processeds.find(p => p.status === 'fulfilled' && p.value && p.value.id === f.id);
          if (processed) {
            f.duration = processed.value.duration;
            f.durationFormatted = processed.value.durationFormatted;
            f.thumbnail = processed.value.thumbnail;
            f.isBroken = processed.value.isBroken;
          }
          f.isLoadingInfo = false;
        }
        const tempValidIds = processeds.filter(p => p.status === 'fulfilled' && !p.value.isBroken).map(p => p.value.id);
        const valid = this.files.find(f => tempValidIds.includes(f.id) && !this.fileHasError(f));
        if (valid) {
          this.localActiveFileId = valid.id;
        }
        // console.log("Updated Videos - this.files: ", this.files);
      });
    },

    checkIsSnapshottedById(id) {
      return this.fileSnapshots.some(cf => cf.id === id);
    },

    // Only revoke the Object URL if the file is not snapshotted (the file is newly inserted), for the file that is snapshotted will be handled in confirmUpload
    revokeObjectURLsOfAllFiles() {
      for (const file of this.files) {
        const isSnapshoted = this.checkIsSnapshottedById(file.id);
        // If the file is not snapshotted, which means the file is newly inserted, revoke the Object URL
        if (!isSnapshoted) {
          this.revokeObjectURLsOfFile(file);
        }
      }
    },

    // Only revoke the Object URL if the file is not snapshotted (the file is newly inserted), for the file that is snapshotted will be handled in confirmUpload
    revokeObjectURLsOfFileById(id) {
      const file = this.files.find(f => f.id === id);
      const isSnapshoted = this.checkIsSnapshottedById(id);
      // If the file is not snapshotted, which means the file is newly inserted, revoke the Object URL
      if (file && !isSnapshoted) {
        this.revokeObjectURLsOfFile(file);
      }
    },

    revokeObjectURLsOfFile(file) {
      if (file.previewSrc && !checkIsCdnLink(file.previewSrc)) {
        // console.log("removeFile - file.previewSrc: ", file.previewSrc);
        URL.revokeObjectURL(file.previewSrc);
      }

      if (this.mediaType === 'video') {
        if (file.thumbnail && file.thumbnail.previewSrc && !checkIsCdnLink(file.thumbnail.previewSrc)) {
          // console.log("removeFile - file.thumbnail.previewSrc: ", file.thumbnail.previewSrc);
          URL.revokeObjectURL(file.thumbnail.previewSrc);
        }
      }
    },

    removeFile(e, id) {
      if (e) e.stopPropagation();

      // Revoke Object URL
      this.revokeObjectURLsOfFileById(id);

      // Remove the file
      this.files = this.files.filter(f => f.id !== id);
      if (this.files.length === 0) {
        this.localActiveFileId = null;
      } else if (this.localActiveFileId === id) {
        this.localActiveFileId = this.files[this.files.length - 1].id;
      }
    },
    handleDiscardClick() {
      this.showDiscardConfirm = true;
    },
    confirmDiscard() {
      this.showDiscardConfirm = false;
      this.discardChanges();
    },
    discardChanges() {
      this.revokeObjectURLsOfAllFiles();
      this.files = [...this.fileSnapshots.map(f => {
        return {
          ...f,
          editState: getDefaultEditState(),
        };
      })];
      this.onCloseModal();
    },
    makeCover(id) {
      const index = this.files.findIndex(f => f.id === id);
      if (index <= 0) return;
      const newFiles = [...this.files];
      const [item] = newFiles.splice(index, 1);
      newFiles.unshift(item);
      this.files = newFiles;
    },
    handleMakeCover() {
      if (this.activeFile) {
        this.makeCover(this.activeFile.id);
      }
    },
    handleImageEditorUpdate(updates) {
      if (!this.localActiveFileId) return;
      const idx = this.files.findIndex(f => f.id === this.localActiveFileId);
      if (idx !== -1) {
        const file = this.files[idx];
        Object.assign(file.editState, updates);
      }
    },
    handleThumbnailUpdate(updates) {
      if (!this.localActiveFileId) return;
      const idx = this.files.findIndex(f => f.id === this.localActiveFileId);
      if (idx !== -1) {
        const file = this.files[idx];
        if (file.thumbnail) {
          Object.assign(file.thumbnail.editState, updates);
        }
      }
    },
    async handleCaptureThumbnail(videoElem) {
      if (!this.localActiveFileId || !videoElem) return;
      const idx = this.files.findIndex(f => f.id === this.localActiveFileId);
      if (idx === -1) return;

      if (videoElem.readyState < 2) {
        await new Promise(resolve => {
          videoElem.oncanplay = resolve;
          setTimeout(resolve, 500);
        });
      }

      this.files[idx].isLoadingInfo = true;
      const originalThumbnailPreviewSrc = this.files[idx].thumbnail?.previewSrc;
      try {
        const { blob, extension } = await captureFrameFromVideo(videoElem);
        if (blob) {
          const fileName = this.files[idx].fileName;
          const videoId = this.files[idx].id;
          const parts = fileName.split('.');
          parts.pop();
          const thumbnailFileName = parts.join('.') + `.${extension}`;
          const newThumbnailFileDataObj = getNewMediaUploaderModalImageDataObj(`${videoId}-thumbnail`, blob, thumbnailFileName, blob.size);

          Object.assign(this.files[idx], {
            ...this.files[idx],
            thumbnail: newThumbnailFileDataObj,
            isBroken: newThumbnailFileDataObj.isBroken
          });

          if (originalThumbnailPreviewSrc && !checkIsCdnLink(originalThumbnailPreviewSrc) && !this.fileSnapshots.some(f => f.thumbnail && f.thumbnail.previewSrc === originalThumbnailPreviewSrc)) {
            URL.revokeObjectURL(originalThumbnailPreviewSrc);
          }
        }
      } catch(e) {
        this.files[idx].isBroken = true;
        this.files[idx].thumbnail.isBroken = true;
        console.error("Capture thumbnail failed", e);
      }
      this.files[idx].isLoadingInfo = false;
    },

    handleUploadThumbnail(file) {
      if (!this.localActiveFileId || !file) return;
      const idx = this.files.findIndex(f => f.id === this.localActiveFileId);
      if (idx === -1) return;

      this.files[idx].isLoadingInfo = true;

      const originalThumbnailPreviewSrc = this.files[idx].thumbnail?.previewSrc;
      const videoId = this.files[idx].id;
      const newThumbnailFileDataObj = getNewMediaUploaderModalImageDataObj(`${videoId}-thumbnail`, file, file.name, file.size);
      Object.assign(this.files[idx], {
        ...this.files[idx],
        thumbnail: newThumbnailFileDataObj,
        isBroken: newThumbnailFileDataObj.isBroken
      });

      if (originalThumbnailPreviewSrc && !checkIsCdnLink(originalThumbnailPreviewSrc) && !this.fileSnapshots.some(f => f.thumbnail && f.thumbnail.previewSrc === originalThumbnailPreviewSrc)) {
        URL.revokeObjectURL(originalThumbnailPreviewSrc);
      }

      this.files[idx].isLoadingInfo = false;
    },
    handleSortStart(e, i) { this.dragItem = i; },
    handleSortEnter(e, i) { this.dragOverItem = i; },
    handleSortEnd() {
      if (this.dragItem === null || this.dragOverItem === null) return;
      const list = [...this.files];
      const [moved] = list.splice(this.dragItem, 1);
      list.splice(this.dragOverItem, 0, moved);
      this.files = list;
      this.dragItem = null;
      this.dragOverItem = null;
    },
    handleDragOver(e) {
      if (this.isFull) return;

      this.isDraggingFile = true;
    },
    handleDragLeave(e) {
      if (this.isFull) return;

      if (e.currentTarget.contains(e.relatedTarget)) return;
      this.isDraggingFile = false;
    },
    handleDrop(e) {
      if (this.isFull) return;

      this.isDraggingFile = false;
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        const selectedFiles = Array.from(droppedFiles);
        if (selectedFiles.length === 0) return;
        if (this.mediaType === 'image') {
          this.processImageFiles(selectedFiles);
        } else if (this.mediaType === 'video') {
          this.processVideoFiles(selectedFiles);
        }
      }
    },

    // For copy from media uploader's parent
    async copyFromOtherUploader(files) {
      this.files = [];

      if (this.mediaType === 'image') {
        this.processImageFiles(files);
      } else if (this.mediaType === 'video') {
        this.processVideoFiles(files);
      }

      await this.confirmUpload(true);
    },

    async confirmUpload(isByPassValidation = false) {
      let valid = [];
      if (isByPassValidation) {
        valid = this.files;
      } else {
        valid = this.files.filter(f => !this.fileHasError(f));
      }
  
      if (valid.length !== this.files.length) return;

      this.isProcessing = true;
      
      if (this.mediaType === 'image') {
        await this.confirmImageUpload(valid);
      } else if (this.mediaType === 'video') {
        await this.confirmVideoUpload(valid);
      }
      
      this.isProcessing = false;
    },

    async confirmImageUpload(valid) {
      const ratio = this.parsedAspectRatio;

      const croppedResults = await Promise.allSettled(valid.filter(f => {
        return !this.fileHasError(f) && (f.editState.isDirty || f.isPendingUpload);
      }).map(async f => {
        const { url: croppedUrl, blob: croppedBlob, fileExtension: croppedFileExtension } = await getCroppedImgInfo(
          f.previewSrc,
          f.editState,
          ratio,
          true
        );

        return {
          id: f.id,
          fileName: f.fileName,
          croppedUrl,
          croppedBlob,
          croppedFileExtension
        }
      }));

      const newImages = [];
      const originalPreviewSrcs = [];
      for (let i = 0; i < this.files.length; i++) {
        const file = this.files[i];
        const cr = croppedResults.find(cr => cr.status === 'fulfilled' && cr.value?.id === file.id)?.value;
        originalPreviewSrcs.push(file.previewSrc);

        // For update successfully cropped image
        if (cr) {
          file.previewSrc = cr.croppedUrl;
          file.fileName = cr.fileName;
          file.pendingUploadFile = cr.croppedBlob;
          file.fileExtension = cr.croppedFileExtension;
          file.editState = getDefaultEditState();
        }

        // For emit
        newImages.push({
          id: file.id,
          previewSrc: file.previewSrc,
          fileName: file.fileName,
          file: file.pendingUploadFile,
          fileExtension: file.fileExtension,
          orderId: i + 1
        });
      }

      // Revoke original images
      for (const originalPreviewSrc of originalPreviewSrcs) {
        if (
          originalPreviewSrc &&
          !checkIsCdnLink(originalPreviewSrc) &&
          !newImages.some(f => f.previewSrc === originalPreviewSrc)
        ) {
          URL.revokeObjectURL(originalPreviewSrc);
        }
      }

      // Revoke snapshoted images
      for (const fs of this.fileSnapshots) {
        if (
          fs.previewSrc &&
          !checkIsCdnLink(fs.previewSrc) &&
          !newImages.some(f => f.previewSrc === fs.previewSrc)
        ) {
          URL.revokeObjectURL(fs.previewSrc);
        }
      }

      this.fileSnapshots = [...this.files.map((f) => ({ ...f }))];
      this.$emit('updateAndClose', newImages);
    },

    async confirmVideoUpload(valid) {
      const ratio = this.parsedAspectRatio;

      const croppedResults = await Promise.allSettled(valid.filter(f => {
        return !this.fileHasError(f) && (f.thumbnail.editState.isDirty || f.thumbnail.isPendingUpload);
      }).map(async v => {
        const f = v.thumbnail;
        const { url: croppedUrl, blob: croppedBlob, fileExtension: croppedFileExtension } = await getCroppedImgInfo(
          f.previewSrc,
          f.editState,
          ratio
        );

        return {
          videoId: v.id,
          fileName: f.fileName,
          croppedUrl,
          croppedBlob,
          croppedFileExtension
        }
      }));

      const newVideos = [];
      const originalPreviewSrcs = [];
      for (let i = 0; i < this.files.length; i++) {
        const file = this.files[i];
        const cr = croppedResults.find(cr => cr.status === 'fulfilled' && cr.value?.videoId === file.id)?.value;
        const original = {
          videoSrc: file.previewSrc,
          thumbnailSrc: file.thumbnail.previewSrc
        }
        originalPreviewSrcs.push(original);

        // For update successfully cropped image
        if (cr) {
          file.thumbnail.previewSrc = cr.croppedUrl;
          file.thumbnail.fileName = cr.fileName;
          file.thumbnail.pendingUploadFile = cr.croppedBlob;
          file.thumbnail.fileExtension = cr.croppedFileExtension;
          file.thumbnail.editState = getDefaultEditState();
        }

        // For emit
        newVideos.push({
          id: file.id,
          previewSrc: file.previewSrc,
          fileName: file.fileName,
          file: file.pendingUploadFile,
          fileExtension: file.fileExtension,
          thumbnail: {
            id: file.thumbnail.id,
            previewSrc: file.thumbnail.previewSrc,
            fileName: file.thumbnail.fileName,
            file: file.thumbnail.pendingUploadFile,
            fileExtension: file.thumbnail.fileExtension
          },
          orderId: i + 1
        });
      }

      // Revoke original videos and thumbnails
      for (const original of originalPreviewSrcs) {
        const { videoSrc: originalPreviewSrc, thumbnailSrc: originalThumbnailPreviewSrc } = original;

        // Revoke original videos
        if (
          originalPreviewSrc &&
          !checkIsCdnLink(originalPreviewSrc) &&
          !newVideos.some(f => f.previewSrc === originalPreviewSrc)
        ) {
          URL.revokeObjectURL(originalPreviewSrc);
        }

        // Revoke original videos thumbnail
        if (
          originalThumbnailPreviewSrc &&
          !checkIsCdnLink(originalThumbnailPreviewSrc) &&
          !newVideos.some(f => f.thumbnail && f.thumbnail.previewSrc === originalThumbnailPreviewSrc)
        ) {
          URL.revokeObjectURL(originalThumbnailPreviewSrc);
        }
      }

      // Revoke snapshoted videos and thumbnails
      for (const fs of this.fileSnapshots) {
        // Revoke snapshoted videos
        if (
          fs.previewSrc &&
          !checkIsCdnLink(fs.previewSrc) &&
          !newVideos.some(f => f.previewSrc === fs.previewSrc)
        ) {
          URL.revokeObjectURL(fs.previewSrc);
        }

        // Revoke snapshoted videos thumbnail
        if (
          fs.thumbnail &&
          fs.thumbnail.previewSrc &&
          !checkIsCdnLink(fs.thumbnail.previewSrc) &&
          !newVideos.some(f => f.thumbnail && f.thumbnail.previewSrc === fs.thumbnail.previewSrc)
        ) {
          URL.revokeObjectURL(fs.thumbnail.previewSrc);
        }
      }

      this.fileSnapshots = [...this.files.map((f) => ({ ...f }))];
      this.$emit('updateAndClose', newVideos);
    },

    openFileExplorer() {
      const fileInput = this.$refs.fileInput;
      if (fileInput) {
        fileInput.click();
      }
    },

    // [BLk] For parent component to open modal
    onOpenModal(mediaId) {
      // console.log("this.files: ", this.files);
      this.localActiveFileId = mediaId;
      this.isModalOpen = true;
    },

    onCloseModal() {
      // console.log("this.files: ", this.files);
      // console.log("this.fileSnapshots: ", this.fileSnapshots);
      this.localActiveFileId = null;
      this.isModalOpen = false;
      this.$refs.videoEditor?.revokeBackgroundUrls();
    },

    getFileDetails(file) {
      if (this.mediaType !== 'video' && file.sizeBytes > 0) {
        return file.sizeFormatted;
      }

      if (this.mediaType === 'video') {
        return `${file.durationFormatted ? file.durationFormatted : "0:00"} • ${file.sizeBytes > 0 ? file.sizeFormatted : "0 MB"}`;
      }

      return '';
    }
  }
};
</script>

<style scoped lang="scss">
/* Reset / Base */
* {
  box-sizing: border-box;
  font-family: "inter", sans-serif;
}

.LoadingContainer {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.LoadingContainer svg {
  height: 20px;
  width: 20px;
}

.modal-editor-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  gap: 1rem;
  border-radius: 1rem;
  overflow: hidden;
}

.error-popup-stack {
  position: absolute;
  top: 4rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width:400px
}

.error-popup {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #FCEBED;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  font-family: "inter", sans-serif;
}

.warning-popup {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  font-family: "inter", sans-serif;
  background-color: #FFFCE5;

}

.warning-popup .error-popup-text {
  color: #B45309;
}

.error-popup-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  align-self: flex-start;
}

.error-popup-content {
  flex: 1;
  min-width: 0;
}
.error-popup-file-name {
  color: rgba(0, 0, 0, 0.60);
}
.error-popup-text {
  color: #CD1515;
  font-size: 0.875rem;
  line-height: 1.4;
  display: block;
  overflow: hidden;

}

.error-popup-prefix {
  font-weight: 700;
}

.error-popup-close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #999;
  display: flex;
  align-items: center;
  transition: color 0.15s;

  &:hover {
    color: #333;
  }
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.popup-fade-enter,
.popup-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.popup-fade-move {
  transition: transform 0.25s ease;
}

.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  padding: 1rem;
}

.modal-window {
  background-color: #F2F3F5;
  width: 100%;
  max-width: 62.5rem;
  max-height: 800px;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  aspect-ratio: 10/7;

}

.modal-header {
  height: 3.5rem;
  padding: 0 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  flex-shrink: 0;
}
.modal-title {
  font-weight: 700;
  font-size: 1.125rem;
  color: #1e293b;
}
.close-btn {
  color: #94a3b8;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
}
.close-btn:hover {
  color: #475569;
}

.modal-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  padding: 1rem;
  gap: 1rem;
}

/* Sidebar */
.modal-sidebar {
  width: 20rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 0.5rem;
}
.modal-sidebar.hidden {
  display: none;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
}
.sidebar-title {
  font-weight: 700;
  color: #334155;
}
.sidebar-title.exceed-limit {
  color: #ef4444;
}

.upload-label {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: color 0.2s;
  margin-bottom: 0;

}
.upload-label.active {
  cursor: pointer;
  color: #4f46e5;
}
.upload-label.active:hover {
  color: #4338ca;
}
.hidden-input {
  display: none;
}

.sidebar-hint {
  font-size: 0.75rem;
  color: #94a3b8;
}

.sidebar-list {
  padding-block: 2px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.sidebar-item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0 2px 0 2px;
}

.sidebar-item {
  position: relative;
  background-color: white;
  padding: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  min-height: 64px;
}
.sidebar-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
.sidebar-item.active {
  border-color: #6366f1;
  box-shadow: 0 0 0 1px #6366f1;
}

.sidebar-item.error-file:not(.active) {
  border-color: #ef4444;
  box-shadow: 0 0 0 1px #ef4444;
}

.drag-handle {
  color: #cbd5e1;
  cursor: move;
}

.item-thumb {
  width: 3rem;
  background-color: #f1f5f9;
  border-radius: 0.25rem;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.item-thumb-round {
  border-radius: 100%;
}
.item-thumb img, .thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.item-thumb .warning-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}
.no-thumb-icon {
  font-size: 20px;
  color: #cbd5e1;
}

.item-info {
  flex: 1;
  min-width: 0;
}
.item-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.item-name.exceed-size {
  color: #ef4444;
}
.item-size {
  font-size: 0.75rem;
  color: #94a3b8;
}

.item-error-message {
  font-size: 0.75rem;
  color: #ef4444;
  padding: 0.25rem 0.5rem;
  margin-left: 0.5rem;
  line-height: 1.4;
}

.item-remove {
  background: transparent;
  border: none;
  color: #cbd5e1;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;
}
.item-remove:hover {
  color: #ef4444;
}

.item-cover-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  position: absolute;
  top: 0;
  right: 0;
  background-color: #2C6ECB;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  text-align: center;
}

/* Editor */
.modal-editor {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: 1rem;
  overflow: hidden;
}


.drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  background-color: rgba(99, 102, 241, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.drop-text {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 1rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.empty-icon {
  color: #475569;
  margin-bottom: 0.5rem;
}
.empty-state-title {
  color: rgba(0, 0, 0, 0.80);
  text-align: center;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
  font-family: "inter", sans-serif;
}
.empty-state-description {
  color:rgba(0, 0, 0, 0.80);
  text-align: center;
  font-size: 1rem;
  font-weight: 400;
  line-height: 130%;
  font-family: "inter", sans-serif;
}
.empty-state-requirement-message {
  color: rgba(0, 0, 0, 0.40);
  text-align: center;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  font-family: "inter", sans-serif;
}
.browse-link {
  color: #4f46e5;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.2s;
}
.browse-link:hover {
  color: #4338ca;
  text-decoration: underline;
}

/* Modal Footer */
.modal-footer {
  height: 4rem;
  background-color: white;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 1.5rem;
  gap: 0.75rem;
  flex-shrink: 0;
}

.btn {
  border: none;
  cursor: pointer;
  font-weight: 600;
  border-radius: 0.5rem;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: rgba(0, 0, 0, 0.20)!important;
}

.btn-primary {
  background-color: #6366f1;
  color: white;
}
.btn-primary:hover:not(:disabled) {
  background-color: #4f46e5;
}

.btn-danger-outline {
  background: transparent;
  color: rgba(0, 0, 0, 0.60);
  padding: 0.5rem 1rem;
  font-weight: 600;
}
.btn-danger-outline:hover {
  color: #ef4444;
  border-radius: 0.5rem;

}

.btn-confirm-action {
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Confirmation Dialog */
.confirm-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.confirm-dialog {
  background-color: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  min-width: 31.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 90%;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.25);
}

.confirm-dialog-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
}

.confirm-dialog-message {
  font-size: 0.875rem;
  color: #64748b;

}

.confirm-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn-cancel {
  background: transparent;
  color: #64748b;
  padding: 0.5rem 1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
}

.btn-cancel:hover {
  color: #ef4444;
}

.btn-confirm {
  padding: 8px 16px;
  border-radius: 4px;

}
</style>